import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, Phone, Apple, Play, Building2, Stethoscope, GraduationCap, Hotel, ShoppingBag, Navigation, CheckCircle, ArrowLeft } from "lucide-react";
import { SEOHead, buildBreadcrumbSchema } from "../components/SEOHead";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.1, duration: 0.6, ease: "easeOut" } }) };

export default function CityPage({ onDownload }) {
  const { slug } = useParams();
  const [city, setCity] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/cities/${slug}`).then(r => { if (!r.ok) throw new Error(); return r.json(); }).then(setCity).catch(() => setCity(null)).finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center"><p className="text-white/40">Loading...</p></div>;
  if (!city) return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center text-center px-6">
      <div>
        <h1 className="font-['Outfit'] text-3xl font-bold text-white mb-4">City Not Found</h1>
        <p className="text-white/40 mb-6">We don't serve this area yet, but we're expanding!</p>
        <Link to="/" className="text-[#E60000] font-semibold hover:text-[#FF3B30]">← Back to Home</Link>
      </div>
    </div>
  );

  const isActive = city.status === "active";
  const isApp = city.booking_method === "app";
  const faqSchema = {
    "@context": "https://schema.org", "@type": "FAQPage",
    "mainEntity": [
      { "@type": "Question", "name": `Is Tripsi available in ${city.name}?`, "acceptedAnswer": { "@type": "Answer", "text": isActive ? `Yes! Tripsi is currently serving ${city.name}, ${city.province}. ${isApp ? "Download the Tripsi app to book rides." : "Call dispatch at +1 647-249-9977 to book."}` : `Tripsi is coming soon to ${city.name}. Download the app to be notified when we launch.` } },
      { "@type": "Question", "name": `How do I book a Tripsi ride in ${city.name}?`, "acceptedAnswer": { "@type": "Answer", "text": isApp ? "Download the Tripsi app from the App Store or Google Play and book directly." : "Call our dispatch at +1 647-249-9977 to book your ride." } },
      { "@type": "Question", "name": `How much does a Tripsi ride cost in ${city.name}?`, "acceptedAnswer": { "@type": "Answer", "text": "Tripsi offers affordable rides without expensive convenience fees. Fares are transparent — what you see is what you pay." } },
    ]
  };
  const localBizSchema = {
    "@context": "https://schema.org", "@type": "LocalBusiness",
    "name": `Tripsi ${city.name}`, "description": city.description,
    "telephone": "+1-647-249-9977", "url": `https://tripsi.ca/cities/${city.slug}`,
    "address": { "@type": "PostalAddress", "addressLocality": city.name, "addressRegion": city.province_code, "addressCountry": "CA" },
    ...(city.lat && city.lng ? { "geo": { "@type": "GeoCoordinates", "latitude": city.lat, "longitude": city.lng } } : {}),
    "areaServed": { "@type": "City", "name": city.name }
  };

  return (
    <div data-testid="city-page" className="min-h-screen bg-[#0A0A0A]">
      <SEOHead
        title={city.meta_title || `Tripsi ${city.name} | Affordable Rides`}
        description={city.meta_description || city.description}
        canonical={`/cities/${city.slug}`}
        jsonLd={[faqSchema, localBizSchema, buildBreadcrumbSchema([{ name: "Home", url: "/" }, { name: "Cities", url: "/#community" }, { name: city.name, url: `/cities/${city.slug}` }])]}
      />

      {/* Hero */}
      <section className="relative py-24 md:py-36 overflow-hidden" style={{ background: "linear-gradient(180deg, #1a0a10 0%, #0A0A0A 100%)" }}>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_30%,rgba(230,0,0,0.08)_0%,transparent_60%)]" />
        <div className="relative max-w-5xl mx-auto px-6 md:px-8">
          <Link to="/" className="inline-flex items-center gap-2 text-white/40 hover:text-white text-sm mb-8 transition-colors"><ArrowLeft size={16} />Back to Home</Link>
          <motion.div initial="hidden" animate="visible">
            <motion.div variants={fadeUp} className="flex items-center gap-3 mb-4">
              <MapPin size={18} className="text-[#E60000]" />
              <span className="text-[#E60000] text-xs font-bold uppercase tracking-[0.2em]">{city.province}</span>
              {!isActive && <span className="text-[10px] font-bold uppercase tracking-wider bg-[#E60000]/20 text-[#E60000] px-2 py-0.5 ml-2">Coming Soon</span>}
              {isActive && <span className="text-[10px] font-bold uppercase tracking-wider bg-green-500/20 text-green-400 px-2 py-0.5 ml-2">Active</span>}
            </motion.div>
            <motion.h1 variants={fadeUp} custom={1} className="font-['Outfit'] text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter text-white mb-6">{city.headline || `Affordable Rides in ${city.name}`}</motion.h1>
            <motion.p variants={fadeUp} custom={2} className="text-white/50 text-base md:text-lg max-w-2xl mb-10">{city.description}</motion.p>
            <motion.div variants={fadeUp} custom={3} className="flex flex-wrap gap-4">
              {isApp ? (
                <>
                  <button onClick={onDownload} className="flex items-center gap-3 bg-[#E60000] hover:bg-[#FF3B30] text-white px-6 py-3.5 font-semibold text-sm transition-all"><Apple size={18} />Download App</button>
                  <button onClick={onDownload} className="flex items-center gap-3 bg-white/5 hover:bg-white/10 border border-white/10 text-white px-6 py-3.5 font-semibold text-sm"><Play size={18} />Google Play</button>
                </>
              ) : (
                <a href="tel:+16472499977" className="flex items-center gap-3 bg-[#E60000] hover:bg-[#FF3B30] text-white px-6 py-3.5 font-semibold text-sm transition-all"><Phone size={18} />Call Dispatch: +1 647-249-9977</a>
              )}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Services */}
      <section className="py-20 md:py-28">
        <div className="max-w-5xl mx-auto px-6 md:px-8">
          <h2 className="font-['Outfit'] text-2xl md:text-3xl font-bold text-white mb-10">Transportation Services in {city.name}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { icon: <Navigation size={20} />, title: "Local Rides", desc: `Affordable point-to-point rides across ${city.name} and surrounding areas.` },
              { icon: <Building2 size={20} />, title: "Airport Transfers", desc: "Reliable airport pickup and drop-off services for travelers." },
              { icon: <Stethoscope size={20} />, title: "Medical Transport", desc: "Safe rides to hospitals, clinics, and medical appointments." },
              { icon: <Hotel size={20} />, title: "Hotel & Hospitality", desc: "Guest transportation for hotels, B&Bs, and resorts." },
              { icon: <GraduationCap size={20} />, title: "Student Rides", desc: "Affordable rides for students commuting to campus." },
              { icon: <ShoppingBag size={20} />, title: "Shopping & Errands", desc: "Convenient rides for grocery runs and shopping trips." },
            ].map((s, i) => (
              <motion.div key={s.title} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={i}
                className="bg-[#141414] border border-white/5 p-6 hover:border-[#E60000]/20 transition-colors">
                <div className="text-[#E60000] mb-3">{s.icon}</div>
                <h3 className="font-['Outfit'] text-base font-bold text-white mb-2">{s.title}</h3>
                <p className="text-white/40 text-sm">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Landmarks & Nearby */}
      {(city.landmarks?.length > 0 || city.nearby_areas?.length > 0) && (
        <section className="py-20 md:py-28 bg-[#141414]">
          <div className="max-w-5xl mx-auto px-6 md:px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
              {city.landmarks?.length > 0 && (
                <div>
                  <h2 className="font-['Outfit'] text-2xl font-bold text-white mb-6">Popular Destinations</h2>
                  <div className="space-y-3">
                    {city.landmarks.map(l => (
                      <div key={l} className="flex items-center gap-3"><CheckCircle size={16} className="text-[#E60000] shrink-0" /><span className="text-white/60 text-sm">{l}</span></div>
                    ))}
                  </div>
                </div>
              )}
              {city.nearby_areas?.length > 0 && (
                <div>
                  <h2 className="font-['Outfit'] text-2xl font-bold text-white mb-6">Nearby Service Areas</h2>
                  <div className="flex flex-wrap gap-3">
                    {city.nearby_areas.map(a => (
                      <span key={a} className="bg-white/5 border border-white/10 px-4 py-2 text-white/60 text-sm">{a}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* FAQ */}
      <section className="py-20 md:py-28">
        <div className="max-w-3xl mx-auto px-6 md:px-8">
          <h2 className="font-['Outfit'] text-2xl md:text-3xl font-bold text-white mb-10">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {[
              { q: `Is Tripsi available in ${city.name}?`, a: isActive ? `Yes! Tripsi is currently serving ${city.name}. ${isApp ? "Download the app to book." : "Call dispatch at +1 647-249-9977."}` : `Tripsi is coming soon to ${city.name}. Download the app to be notified.` },
              { q: `How do I book a ride in ${city.name}?`, a: isApp ? "Download the Tripsi app from the App Store or Google Play and book directly in the app." : "Call our dispatch team at +1 647-249-9977 to book your ride. We're available for bookings and inquiries." },
              { q: "How much does a Tripsi ride cost?", a: "Tripsi offers affordable rides without expensive convenience fees. Our fares are transparent — what you see is what you pay." },
              { q: "Is Tripsi safe?", a: "Safety is our top priority. All drivers are vetted, rides are tracked, and we maintain professional standards for every trip." },
              { q: `Can I become a Tripsi driver in ${city.name}?`, a: "Yes! We're onboarding drivers across Canada. Visit our Drivers section to apply." },
            ].map((faq, i) => (
              <div key={i} className="bg-[#141414] border border-white/5 p-5">
                <h3 className="font-['Outfit'] text-sm font-bold text-white mb-2">{faq.q}</h3>
                <p className="text-white/40 text-sm">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-28 bg-[#E60000]">
        <div className="max-w-3xl mx-auto px-6 md:px-8 text-center">
          <h2 className="font-['Outfit'] text-3xl md:text-4xl font-bold text-white mb-4">Ready to ride in {city.name}?</h2>
          <p className="text-white/80 text-base mb-8">{isApp ? "Download the app and book your first ride." : "Call dispatch to book your ride today."}</p>
          {isApp ? (
            <div className="flex flex-wrap gap-4 justify-center">
              <button onClick={onDownload} className="flex items-center gap-3 bg-white text-[#E60000] hover:bg-white/90 px-8 py-4 font-bold text-sm"><Apple size={18} />App Store</button>
              <button onClick={onDownload} className="flex items-center gap-3 border-2 border-white text-white hover:bg-white/10 px-8 py-4 font-bold text-sm"><Play size={18} />Google Play</button>
            </div>
          ) : (
            <a href="tel:+16472499977" className="inline-flex items-center gap-3 bg-white text-[#E60000] hover:bg-white/90 px-8 py-4 font-bold text-sm"><Phone size={18} />+1 647-249-9977</a>
          )}
        </div>
      </section>
    </div>
  );
}
